import { MutationTree } from "vuex";
import {
    IPropertyModel,
    IPropertyMultModel,
    IPropertyDescriptionModel,
    IPropertySmartSuggest
} from "../../../models/property";
import propertyEditTypesStore from "./property-edit-types.store";
import { storeTypes } from "../..";
import Vue from "vue";
import { IPropertyEditState } from "./property-states.store";
import { DataHelper } from "../../../utils/data-helper";
import { itemStateConstant } from "../../../common";
import { IMediaPubPlan } from "../../../models/property/property-publish-plan.model";
import { deepClone } from "fast-json-patch";

const mutations: MutationTree<IPropertyEditState> = <
    MutationTree<IPropertyEditState>
>{
    [storeTypes.Set.ASSIGNVALUE]: (
        state: IPropertyEditState,
        property: IPropertyModel
    ) => {
        Object.keys(property).forEach(key =>
            Vue.set(state.property, key, property[key])
        );
    },
    [storeTypes.Set.ASSIGNVALUE_TO_ORIGINOBJECT]: (
        state: IPropertyEditState,
        property: IPropertyModel
    ) => {
        Object.keys(property).forEach(key =>
            Vue.set(state.originalProperty, key, property[key])
        );
    },
    [propertyEditTypesStore.Set.ASSIGNVALUE_PROPERTY_MANAGEMENT]: (
        state: IPropertyEditState,
        payload: { [key: string]: any; value: any }
    ) => {
        Object.keys(payload).forEach(key =>
            Vue.set(state.property.Management, key, payload[key])
        );
    },
    [propertyEditTypesStore.Set.ASSIGNVALUE_PROPERTY_PROSPECTION]: (
        state: IPropertyEditState,
        payload: { [key: string]: any; value: any }
    ) => {
        Object.keys(payload).forEach(key =>
            Vue.set(state.property.Prospection, key, payload[key])
        );
    },
    [propertyEditTypesStore.Set.PROPERTY]: (
        state: IPropertyEditState,
        property: IPropertyModel
    ) => {
        state.property = property;
    },
    [propertyEditTypesStore.Set.ORIGIN_PROPERTY]: (
        state: IPropertyEditState,
        property: IPropertyModel
    ) => {
        state.originalProperty = property;
    },
    [propertyEditTypesStore.Set.ORIGIN_PROPERTY_AND_DIFF]: (
        state: IPropertyEditState,
        payload: { property: IPropertyModel; diff: IPropertyModel }
    ) => {
        const { property, diff } = payload;
        state.property = deepClone(property);
        state.originalProperty = deepClone(property);
        Object.keys(diff).forEach(key =>
            Vue.set(state.property, key, diff[key])
        );
    },
    [propertyEditTypesStore.Set.PROPERTY_SUBCATEGORY]: (
        state: IPropertyEditState,
        subcategory: IPropertyMultModel
    ) => {
        state.propertySubCategory = subcategory;
    },
    [propertyEditTypesStore.Set.PROPERTY_DESCRIPTION]: (
        state: IPropertyEditState,
        description: IPropertyDescriptionModel
    ) => {
        state.propertyDescription = description;
    },
    [propertyEditTypesStore.Set.ASSIGNVALUE_DESCRIPTION]: (
        state: IPropertyEditState,
        payload: { [key: string]: any; value: any }
    ) => {
        Object.keys(payload).forEach(key =>
            Vue.set(state.propertyDescription, key, payload[key])
        );
    },
    [propertyEditTypesStore.Set.ASSIGNVALUE_SUBCATEGORY]: (
        state: IPropertyEditState,
        payload: any
    ) => {
        let typeOfProps = state.property.TypeOfProps;

        if (payload.Action === itemStateConstant.Added) {
            let subMainCategory = typeOfProps.find(
                type => type.IsMain === true
            );

            subMainCategory = {FieldInfo: payload.defaultCategory, FieldId: payload.defaultCategory.Id};

            typeOfProps.push({
                ...subMainCategory,
                IsMain: !payload.isHaveMainCategory,
                FieldId: subMainCategory.FieldId,
                ItemState: itemStateConstant.Added
            });
        } else if (payload.Action === itemStateConstant.Deleted) {
            if (
                typeOfProps[payload.SubcategoryIndex].ItemState ===
                itemStateConstant.Added
            ) {
                typeOfProps.splice(payload.SubcategoryIndex, 1);
            } else {
                typeOfProps[payload.SubcategoryIndex].ItemState =
                    itemStateConstant.Deleted;
            }

            // Update MediaPubPlans
            state.property.MediaPubPlans = state.property.MediaPubPlans.filter(
                (x) => {
                    return x.TypeOfPropId !== payload.FieldId.FieldId})
        } else if (payload.IsMain) {
            if (typeOfProps && typeOfProps.length > 0) {
                let subOldMainCategoryIndex = typeOfProps.findIndex(type => {
                    return type.IsMain === true;
                });
    
                // Update IsMain for old main category
                typeOfProps[subOldMainCategoryIndex].IsMain = false;
                if (
                    typeOfProps[subOldMainCategoryIndex].ItemState !==
                    itemStateConstant.Added
                ) {
                    typeOfProps[subOldMainCategoryIndex].ItemState =
                        itemStateConstant.Modified;
                }
                // Update IsMain for new main category
                typeOfProps[payload.SubcategoryIndex].IsMain = true;
    
                if (payload.CategoryInfo) {
                    typeOfProps[payload.SubcategoryIndex].FieldInfo = {
                        ...payload.CategoryInfo
                    };
                    typeOfProps[payload.SubcategoryIndex].FieldId =
                        payload.CategoryInfo.Id;
                }
                if (
                    typeOfProps[payload.SubcategoryIndex].ItemState !==
                    itemStateConstant.Added
                ) {
                    typeOfProps[payload.SubcategoryIndex].ItemState =
                        itemStateConstant.Modified;
                }
            } else {
                typeOfProps.push({
                    FieldInfo: payload.CategoryInfo,
                    FieldId: payload.CategoryInfo.Id,
                    IsMain: true,
                    ItemState: itemStateConstant.Added
                });
            }

            Vue.set(state.property, "CategoryTypeId", payload.CategoryInfo.CategoryTypeId);

            Vue.set(
                state.property,
                "TypeOfPropertyId",
                payload.CategoryInfo.Id
            );
        } else {
            typeOfProps[payload.SubcategoryIndex].FieldInfo = {
                ...payload.CategoryInfo
            };
            typeOfProps[payload.SubcategoryIndex].FieldId = payload.FieldId;

            if (
                typeOfProps[payload.SubcategoryIndex].ItemState !==
                itemStateConstant.Added
            ) {
                typeOfProps[payload.SubcategoryIndex].ItemState =
                    itemStateConstant.Modified;
            }

            if (typeOfProps[payload.SubcategoryIndex].IsMain) {
                Vue.set(state.property, "CategoryTypeId", payload.CategoryInfo.CategoryTypeId);
            }

            Vue.set(state.property, "TypeOfPropertyId", payload.FieldId);
        }

        // Skip for Deleted — that branch above already filters MediaPubPlans to remove only the
        // affected subcategory. Other branches change the type itself, so wipe and let the
        // publish-plan tab regenerate on next visit. HasSelectedPortal follows the wipe.
        if (payload.Action !== itemStateConstant.Deleted) {
            state.MediaPubPlans = []
            Vue.set(state.property, "MediaPubPlans", []);
            Vue.set(state.property, "HasSelectedPortal", false);
            if (state.originalProperty) {
                Vue.set(state.originalProperty, "MediaPubPlans", []);
                Vue.set(state.originalProperty, "HasSelectedPortal", false);
            }
        }

        Vue.set(state.property, "TypeOfProps", typeOfProps);
    },
    [propertyEditTypesStore.Set.LIST_OF_CRITERIAS]: (
        state: IPropertyEditState,
        listOfCriterias: IPropertySmartSuggest[]
    ) => {
        state.listOfCriterias = listOfCriterias;
    },
    [propertyEditTypesStore.Set.SMART_SEARCH_KEYWORD]: (
        state: IPropertyEditState,
        keyword: string
    ) => {
        state.prevSmartSearchKeyword = keyword;
    },
    [propertyEditTypesStore.Set.UPDATE_PROPERTY_PROP_ELEMENT]: (
        state: IPropertyEditState,
        payload: any
    ) => {
        if (state.property[payload.prop]) {
            const element = state.property[payload.prop][payload.index];
            element.ItemState !== itemStateConstant.Added &&
                (payload.data.ItemState = itemStateConstant.Modified);
            DataHelper.mergeData(element, payload.data);

            //Update MediaPubPlans state
            if (payload.prop === "MediaPubPlans") {
                const mediaPubPlansStateIndex = state.MediaPubPlans.findIndex(x => x.ObjectId === payload.objectId);
                const stateElement = state.MediaPubPlans[mediaPubPlansStateIndex].MediaPublicPlans[payload.index];
                DataHelper.mergeData(stateElement, payload.data);
            }
        }
    },
    [propertyEditTypesStore.Set.ADD_PROPERTY_PROP_ELEMENT]: (
        state: IPropertyEditState,
        payload: any
    ) => {
        if (state.property[payload.prop]) {
            payload.data.ItemState = itemStateConstant.Added;
            state.property[payload.prop].push(payload.data);
        }
    },
    [propertyEditTypesStore.Set.DELETE_PROPERTY_PROP_ELEMENT]: (
        state: IPropertyEditState,
        payload: any
    ) => {
        if (state.property[payload.prop]) {
            const element = state.property[payload.prop][payload.index];

            if (element) {
                if (element.ItemState == itemStateConstant.Added) {
                    state.property[payload.prop].splice(payload.index, 1);
                } else {
                    element.ItemState = itemStateConstant.Deleted;
                }
            }
        }
    },
    [propertyEditTypesStore.Set.REMOVE_MEDIA_PUBLIC_PLANS]: (
        state: IPropertyEditState,
        propertyId: number
    ) => {

        let index = state.MediaPubPlans.findIndex(
            x => x.ObjectId === propertyId
        );

        if (index > -1) {
            state.MediaPubPlans[index].MediaPublicPlans.splice(index, 1);
        }
    },
    [propertyEditTypesStore.Set.MEDIA_PUBLIC_PLANS]: (
        state: IPropertyEditState,
        payload: { ObjectId: number; MediaPublicPlans: IMediaPubPlan[] }
    ) => {
        const mediaPubPlansIndex = state.MediaPubPlans && state.MediaPubPlans && state.MediaPubPlans.length && state.MediaPubPlans.findIndex((x) => x.ObjectId === payload.ObjectId);
        if (mediaPubPlansIndex !== null && mediaPubPlansIndex > -1) {
            state.MediaPubPlans[mediaPubPlansIndex] = deepClone(payload);
        } else {
            if (state.MediaPubPlans === null) {
                state.MediaPubPlans = [];
            }
            state.MediaPubPlans.push(payload);
        }

        state.property.MediaPubPlans = deepClone(state.originalProperty.MediaPubPlans)
    },
    [propertyEditTypesStore.Set.UPDATE_MEDIA_PUBLIC_PLANS]: (
        state: IPropertyEditState,
        payload: any
    ) => {
        // debugger;
        let mediaPubPlansIndex = state.MediaPubPlans.findIndex((x) => x.ObjectId === payload.ObjectId);
        const mediaPubPlansChildIndex = state.MediaPubPlans.findIndex((x) => x.ObjectId === payload.data?.ParentObjectId);
        if (mediaPubPlansIndex === -1) {
            mediaPubPlansIndex = mediaPubPlansChildIndex;
        }
        if (mediaPubPlansIndex > -1) {
            const indexOfMediaPubPlans = state.MediaPubPlans[mediaPubPlansIndex].MediaPublicPlans.findIndex(x => x.Id === payload.data.Id && x.MediaId === payload.data.MediaId && x.TypeOfPropId === payload.data.TypeOfPropId);
            if (indexOfMediaPubPlans > -1) {
                state.MediaPubPlans[mediaPubPlansIndex].MediaPublicPlans[indexOfMediaPubPlans] = deepClone(payload.data);
            } else {
                state.MediaPubPlans[mediaPubPlansIndex].MediaPublicPlans.push(payload.data)
            }
        }

        // Set data change in property entity — include Id so projects with main+child plans
        // sharing the same MediaId+TypeOfPropId update the right row (matches the cache lookup above).
        let mediaPropertyIndex = state.property.MediaPubPlans.findIndex(
            x =>
                x.Id === payload.data.Id &&
                x.MediaId === payload.data.MediaId &&
                x.TypeOfPropId === payload.data.TypeOfPropId &&
                x.ItemState !== itemStateConstant.Deleted
        );
        if (mediaPropertyIndex > -1) {
            if (state.property.MediaPubPlans[mediaPropertyIndex].Id > 0) {
                payload.data.ItemState = itemStateConstant.Modified;
                state.property.MediaPubPlans[mediaPropertyIndex] = deepClone(payload.data)
            } else {
                payload.data.ItemState = itemStateConstant.Added;
                state.property.MediaPubPlans[mediaPropertyIndex] = payload.data
            }
        } else {
            payload.data.ItemState = itemStateConstant.Added;
            state.property.MediaPubPlans.push(payload.data)
        }

        // Mirror what the UPDATE_PROPERTY_PROP_ELEMENT action used to chain — keep
        // HasSelectedPortal in sync so general-general's publish gating stays correct.
        const hasSelectedPortal = !!(state.property.MediaPubPlans || []).find(p => p.IsSelected === true);
        state.property.HasSelectedPortal = hasSelectedPortal;
        if (state.originalProperty) {
            state.originalProperty.HasSelectedPortal = hasSelectedPortal;
        }
    },
    [propertyEditTypesStore.Set.HAS_SELECTED_PORTAL]: (
        state: IPropertyEditState,
        payload: IMediaPubPlan[]
    ) => {
        const hasSelect = state.property.MediaPubPlans
            ? state.property.MediaPubPlans.find(pub => pub.IsSelected == true)
            : false;
        if (hasSelect) {
            state.property.HasSelectedPortal = true;
            state.originalProperty.HasSelectedPortal = true;
        } else {
            state.property.HasSelectedPortal = false;
            state.originalProperty.HasSelectedPortal = false;
        }
    },
    [storeTypes.Set.ASSIGNVALUE_PROJECT_GENERAL]: (
        state: IPropertyEditState,
        property: IPropertyModel
    ) => {
        Object.keys(property).forEach(key =>
            Vue.set(state.property.ProjectGeneral, key, property[key])
        );
    },
    [storeTypes.Set.ASSIGNVALUE_PROJECT_DESCRIPTIONS]: (
        state: IPropertyEditState,
        payload: { index: number; value: Object }
    ) => {
        let projectDescription =
            state.property.ProjectDescriptions[payload.index];
        if (projectDescription) {
            projectDescription.ItemState = 2;
            Object.keys(payload.value).forEach(
                key => (projectDescription[key] = payload.value[key])
            );
            Vue.set(
                state.property.ProjectDescriptions,
                payload.index,
                projectDescription
            );
        }
    },
    [storeTypes.Set.ASSIGNVALUE_PROJECT_BUILDING]: (
        state: IPropertyEditState,
        payload: { index: number; value: Object }
    ) => {
        let projectBuilding = state.property.ProjectBuildings[payload.index];
        if (projectBuilding) {
            projectBuilding.ItemState = projectBuilding.Id > 0 ? 2 : 1; // TODO 1 => add, 2 => Modified, 3 => Delete, 0 => Unchanged;
            Object.keys(payload.value).forEach(key => {
                if (payload.value[key] instanceof Object) {
                    Object.keys(payload.value[key]).forEach(index => {
                        projectBuilding[key][index] = payload.value[key][index];
                    });
                } else {
                    projectBuilding[key] = payload.value[key];
                }
            });
            Vue.set(
                state.property.ProjectBuildings,
                payload.index,
                projectBuilding
            );
        }
    },
    [storeTypes.Set.ASSIGNVALUE_PROJECT_BUILDING_NEW]: (
        state: IPropertyEditState,
        payload: { index: number; value: any }
    ) => {
        let projectBuilding = state.property.ProjectBuildings[payload.index];
        if (!projectBuilding || projectBuilding.Id === null) {
            if (!payload.value.OrderByBuilding)
                payload.value.OrderByBuilding = 0;
            // TODO 1 => add, 2 => Modified, 3 => Delete, 0 => Unchanged;
            Vue.set(
                state.property.ProjectBuildings,
                payload.index,
                payload.value
            );
        }
    },
    [storeTypes.Set.ASSIGNVALUE_PROJECT_BUILDING_REMOVE]: (
        state: IPropertyEditState,
        payload: { index: number }
    ) => {
        let availableList = state.property.ProjectBuildings.filter(
            x => x.ItemState !== 3
        );
        let projectBuilding = availableList[payload.index];
        if (projectBuilding) {
            projectBuilding.ItemState = 3; // TODO 1 => add, 2 => Modified, 3 => Delete, 0 => Unchanged;
            let realIndex = state.property.ProjectBuildings.findIndex(
                e => e.Id === projectBuilding.Id
            );
            if (projectBuilding.Id > 0) {
                Vue.set(
                    state.property.ProjectBuildings,
                    realIndex,
                    projectBuilding
                );
            } else {
                state.property.ProjectBuildings.splice(realIndex, 1);
            }
        }
    },
    [storeTypes.Set.ASSIGNVALUE_TO_ORIGINBUILDING_UNIT]: (
        state: IPropertyEditState,
        payload: { id: number; value: any }
    ) => {
        if (state.originalProperty.ProjectBuildings) {
            let projectBuilding = state.originalProperty.ProjectBuildings.find(
                p => p.Id === payload.id
            );
            const index = state.originalProperty.ProjectBuildings.findIndex(
                x => x.Id === payload.id
            );
            if (projectBuilding && index > -1) {
                Object.keys(payload.value).forEach(key =>
                    Vue.set(
                        state.originalProperty.ProjectBuildings[index],
                        key,
                        payload.value[key]
                    )
                );
            }
        }
    },
    [storeTypes.Set.ASSIGNVALUE_PROJECT_BUILDING_UNITS]: (
        state: IPropertyEditState,
        payload: { id: number; value: Object }
    ) => {
        let projectBuilding = state.property.ProjectBuildings
            ? state.property.ProjectBuildings.find(p => p.Id === payload.id)
            : null;
        if (projectBuilding) {
            const index = state.property.ProjectBuildings.findIndex(
                x => x.Id === projectBuilding.Id
            );
            Object.keys(payload.value).forEach(key => {
                if (payload.value[key] instanceof Object) {
                    Object.keys(payload.value[key]).forEach(i => {
                        projectBuilding[key][i] = payload.value[key][i];
                    });
                } else {
                    projectBuilding[key] = payload.value[key];
                }
            });
            Vue.set(state.property.ProjectBuildings, index, projectBuilding);
        }
    },
    [propertyEditTypesStore.Set.RESET_PICTURE_TAB]: (
        state: IPropertyEditState
    ) => {
        state.property.Pictures = deepClone(state.originalProperty.Pictures);
    },
    [propertyEditTypesStore.Set.SET_IS_SAVING]: (state: IPropertyEditState, value: boolean) => {
        state.isSaving = value;
    },
};

export default mutations;
